import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BotsActivityOverviewResponse } from '../../../shared/types/bots-activity/BotsActivityOverviewResponse';
import { BotsActivityTimeseriesResponse } from '../../../shared/types/bots-activity/BotsActivityTimeseriesResponse';
import { getPlatformDisplayName } from '../../../shared/constants/botsActivityColors';

export const ALL_VALUE = '__ALL__';

export const BOT_TYPE_OPTIONS = ['Training', 'Search', 'User query', 'Other'] as const;
export type BotType = (typeof BOT_TYPE_OPTIONS)[number];

const TRAINING_BOTS = new Set([
  'GPTBot',
  'ClaudeBot',
  'anthropic-ai',
  'Bytespider',
  'CCBot',
  'Meta-ExternalAgent',
  'Google-Extended',
  'Applebot-Extended',
  'Amazonbot',
  'Ai2Bot',
  'cohere-ai',
]);
const SEARCH_BOTS = new Set([
  'Googlebot',
  'Bingbot',
  'PerplexityBot',
  'OAI-SearchBot',
  'ChatGPT-Search',
]);
const USER_QUERY_BOTS = new Set([
  'ChatGPT-User',
  'Claude-User',
  'Perplexity-User',
  'Gemini-Deep-Research',
]);

export const classifyBotType = (botName: string): BotType => {
  if (TRAINING_BOTS.has(botName)) return 'Training';
  if (SEARCH_BOTS.has(botName)) return 'Search';
  if (USER_QUERY_BOTS.has(botName)) return 'User query';
  return 'Other';
};

export const useBotsActivityFilters = (
  overview: BotsActivityOverviewResponse | undefined,
  timeseries?: BotsActivityTimeseriesResponse
) => {
  const { t } = useTranslation();
  const [platformFilter, setPlatformFilter] = useState<string>(ALL_VALUE);
  const [botTypeFilter, setBotTypeFilter] = useState<string>(ALL_VALUE);
  const [botFilter, setBotFilter] = useState<string>(ALL_VALUE);

  const platformOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of overview?.platforms ?? []) {
      if (row.platform) set.add(getPlatformDisplayName(row.platform));
    }
    return [
      { label: t('botsActivity.allPlatforms'), value: ALL_VALUE },
      ...Array.from(set)
        .sort()
        .map((label) => ({ label, value: label })),
    ];
  }, [overview, t]);

  const botTypeOptions = useMemo(
    () => [
      { label: t('botsActivity.allBotTypes'), value: ALL_VALUE },
      ...BOT_TYPE_OPTIONS.map((label) => ({ label, value: label })),
    ],
    [t]
  );

  const botOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of overview?.platforms ?? []) {
      if (row.botName) set.add(row.botName);
    }
    return [
      { label: t('botsActivity.allBots'), value: ALL_VALUE },
      ...Array.from(set)
        .sort()
        .map((label) => ({ label, value: label })),
    ];
  }, [overview, t]);

  // Shared predicate — every dataset filters on the same axes (platform / bot
  // type / bot). Centralising it keeps timeseries, overview and content panels
  // in sync when the user flips a filter.
  const matchesFilters = useMemo(() => {
    return (platform: string, botName: string): boolean => {
      if (platformFilter !== ALL_VALUE && getPlatformDisplayName(platform) !== platformFilter)
        return false;
      if (botFilter !== ALL_VALUE && botName !== botFilter) return false;
      if (botTypeFilter !== ALL_VALUE && classifyBotType(botName) !== botTypeFilter) return false;
      return true;
    };
  }, [platformFilter, botFilter, botTypeFilter]);

  const anyFilterActive =
    platformFilter !== ALL_VALUE || botFilter !== ALL_VALUE || botTypeFilter !== ALL_VALUE;

  const filteredOverview = useMemo<BotsActivityOverviewResponse | undefined>(() => {
    if (!overview) return undefined;
    return { platforms: overview.platforms.filter((row) => matchesFilters(row.platform, row.botName)) };
  }, [overview, matchesFilters]);

  const filteredTimeseries = useMemo<BotsActivityTimeseriesResponse | undefined>(() => {
    if (!timeseries) return undefined;
    if (!anyFilterActive) return timeseries;
    return {
      timeseries: timeseries.timeseries.filter((p) => matchesFilters(p.platform, p.botName)),
    };
  }, [timeseries, matchesFilters, anyFilterActive]);

  // Bots allowed by the current filter combination, taken from the overview
  // (the only response that exposes per-bot platform mapping). Passed to the
  // content queries as a backend filter (`?bots=...`) so folder/URL counts are
  // exact for the current selection. `null` = no filter (fetch everything);
  // `[]` = filter matches no bots (callers should render an empty list without
  // hitting the backend).
  const allowedBotNames = useMemo<string[] | null>(() => {
    if (!anyFilterActive) return null;
    const set = new Set<string>();
    for (const row of overview?.platforms ?? []) {
      if (matchesFilters(row.platform, row.botName)) set.add(row.botName);
    }
    return Array.from(set).sort();
  }, [overview, matchesFilters, anyFilterActive]);

  return {
    platformFilter,
    setPlatformFilter,
    botTypeFilter,
    setBotTypeFilter,
    botFilter,
    setBotFilter,
    platformOptions,
    botTypeOptions,
    botOptions,
    filteredOverview,
    filteredTimeseries,
    allowedBotNames,
    anyFilterActive,
  };
};
